import {
  DiagramAnyObjectId,
  ClientDiagram,
  ClientDiagramConnection,
  ClientDiagramNode,
  serializeAnyObjectId,
} from "./types";
import {Vec2} from "./math/vec2";
import {SerializedKeyMap} from "./serializedKeyMap";
import {SerializedKeySet} from "./serializedKeySet";
import {DiagramConnectionId, DiagramNodeId} from "platformed-client/types";
import {generateConnectionPath, getConnectionWaypoints} from "./connectionPath";

/**
 * A simple spatial index that maps each grid cell coordinate to the list of
 * objects present in the cell. It's many-to-many: an object can span multiple
 * cells, and each cell can contain multiple objects.
 */
export class SpatialIndex {
  private cellsToObjects: SerializedKeyMap<Vec2, SerializedKeySet<DiagramAnyObjectId>> = new SerializedKeyMap(v =>
    v.key(),
  );

  private objectsToCells: SerializedKeyMap<DiagramAnyObjectId, SerializedKeySet<Vec2>> = new SerializedKeyMap(
    serializeAnyObjectId,
  );

  static fromDiagram(diagram: ClientDiagram): SpatialIndex {
    const self = new SpatialIndex();

    for (const node of diagram.nodes.values()) {
      self.putNode(node);
    }

    for (const connection of diagram.connections.values()) {
      self.putConnection(connection, diagram.nodes, diagram.connections);
    }

    return self;
  }

  remove(id: DiagramAnyObjectId) {
    const cells = this.objectsToCells.get(id);
    if (cells == null) return;

    // Remove this object from all cells
    for (const cell of cells) {
      const objects = this.cellsToObjects.get(cell);
      if (objects == null) throw new Error("Cell not found");

      objects.delete(id);

      if (objects.size === 0) {
        this.cellsToObjects.delete(cell);
      }
    }

    this.objectsToCells.delete(id);
  }

  private put(id: DiagramAnyObjectId, allCoords: Vec2[]) {
    this.remove(id);

    for (const coords of allCoords) {
      const entry = this.cellsToObjects.get(coords);

      if (entry == null) {
        this.cellsToObjects.set(coords, new SerializedKeySet(serializeAnyObjectId, [id]));
      } else {
        entry.add(id);
      }
    }

    this.objectsToCells.set(id, new SerializedKeySet(k => k.key(), allCoords));
  }

  putNode(node: ClientDiagramNode) {
    const id: DiagramAnyObjectId = {type: "Node", id: node.id};
    this.put(id, [node.position]);
  }

  putConnection(
    connection: ClientDiagramConnection,
    nodes: ReadonlyMap<DiagramNodeId, ClientDiagramNode>,
    connections: ReadonlyMap<DiagramConnectionId, ClientDiagramConnection>,
  ) {
    const id: DiagramAnyObjectId = {type: "Connection", id: connection.id};

    const waypoints = getConnectionWaypoints(connection, nodes);
    const path = generateConnectionPath(waypoints);

    this.put(id, path);

    // Remove existing anchors if the connection previously existed.
    const existingConnection = connections.get(connection.id);
    if (existingConnection != null) {
      for (let i = 0; i < existingConnection.anchors.length; ++i) {
        this.remove({type: "ConnectionAnchor", id: existingConnection.id, index: i});
      }
    }

    // Add new connection anchors
    for (let i = 0; i < connection.anchors.length; ++i) {
      const anchor = connection.anchors[i];
      this.put({type: "ConnectionAnchor", id: connection.id, index: i}, [anchor.position]);
    }
  }

  getNode(coords: Vec2): DiagramNodeId | null {
    const objects = this.cellsToObjects.get(coords);
    if (objects == null) return null;

    for (const object of objects) {
      if (object.type === "Node") return object.id;
    }

    return null;
  }

  getAll(coords: Vec2): DiagramAnyObjectId[] {
    const set = this.cellsToObjects.get(coords);
    return set ? [...set] : [];
  }

  getFirst(coords: Vec2): DiagramAnyObjectId | null {
    const all = this.getAll(coords);

    all.sort((a, b) => {
      const PRIORITY_MAP = {Node: 1, ConnectionAnchor: 2, Connection: 3, Region: 4};

      return PRIORITY_MAP[a.type] - PRIORITY_MAP[b.type];
    });

    return all.length > 0 ? all[0] : null;
  }
}
